/**
 * useSearchUsers Hook
 * Core state management for search users sheet
 * Matches Flutter implementation: lib/presentation/bottom_sheet/search_users_bottom_sheet.dart
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import type { PageCursor } from '@/data/types/json';
import toast from 'react-hot-toast';
import { useDebounce } from '@/presentation/hooks/useDebounce';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { getAuthFirebaseUserUseCase } from '@/domain/usecases/user/getAuthFirebaseUserUseCase';
import { createGetSubscriptionsUseCase } from '@/domain/usecases/subscription/getSubscriptionsUseCase';
import { createSearchSubscriptionsUseCase } from '@/domain/usecases/subscription/searchSubscriptionsUseCase';
import { createShareWithFriendsUseCase } from '@/domain/usecases/share/shareWithFriendsUseCase';
import { SheetService } from '@/presentation/components/common/sheet/service/SheetService';
import { filterDuplicateSubscriptions } from '../filterDuplicateSubscriptions';
import type { ExclusiveContentSubscriptionEntity } from '@/domain/entities/exclusiveContentSubscription/exclusiveContentSubscriptionEntity';
import type { SearchSubscriptionEntity } from '@/domain/entities/subscription/searchSubscriptionEntity';
import type { ShareInfoEntity } from '@/domain/entities/info/shareInfoEntity';
import { SERVER_FETCH_CONSTANTS } from '@/domain/constants/serverFetchConstants';
import { COMMON_CONSTANTS } from '@/presentation/constants/commonConstants';
import { appLogger } from '@/domain/services/analytics/appLogger';

export interface UseSearchUsersProps {
  /**
   * Share info for the message/share flow. Optional: the mention picker mode
   * of SearchUsersSheet does not invoke `shareWithSelected`, so no share info
   * is required in that case.
   */
  shareInfo?: ShareInfoEntity;
}

export interface UseSearchUsersResult {
  subscriptions: SearchSubscriptionEntity[];
  searchResults: SearchSubscriptionEntity[];
  selectedSubscriptions: ExclusiveContentSubscriptionEntity[];
  loading: boolean;
  isSearchPending: boolean;
  hasMore: boolean;
  hasMoreSearchResults: boolean;
  searchKeyword: string;
  isSharing: boolean;
  setSearchKeyword: (keyword: string) => void;
  toggleSubscriptionSelection: (subscription: ExclusiveContentSubscriptionEntity) => void;
  removeSubscription: (subscription: ExclusiveContentSubscriptionEntity) => void;
  loadMoreSubscriptions: () => void;
  loadMoreSearchResults: () => void;
  /**
   * Trigger the share use case with the current selection and the composer's
   * message text. `MessageInput` is uncontrolled (self-owns its draft) so the
   * caller passes the current text in via this argument; this hook no longer
   * mirrors the composer's keystroke state.
   */
  shareWithSelected: (messageText: string) => void;
}

export function useSearchUsers({ shareInfo }: UseSearchUsersProps): UseSearchUsersResult {
  // State
  const [subscriptions, setSubscriptions] = useState<ExclusiveContentSubscriptionEntity[]>([]);
  const [searchResults, setSearchResults] = useState<ExclusiveContentSubscriptionEntity[]>([]);
  const [selectedSubscriptions, setSelectedSubscriptions] = useState<ExclusiveContentSubscriptionEntity[]>([]);
  const [searchKeyword, setSearchKeyword] = useState('');
  // Flutter keeps two independent in-flight flags for this sheet and neither function reads the
  // other's: `_isFetchingSubscriptions` (search_users_bottom_sheet.dart:56, read at :222) and
  // `_isFetchingSearchSubscriptions` (:65, read at :242). Collapsing them onto one shared flag let
  // a subscriptions page in flight swallow the debounced search below — the effect fires
  // performSearch once per keyword change and never retries, so the sheet parked on an empty list
  // with neither spinner nor empty-state copy. `loading` stays the union, so every consumer
  // (SearchUsersContent's `loading || isSearchPending`, the in-list spinner) is unchanged.
  const [isFetchingSubscriptions, setIsFetchingSubscriptions] = useState(false);
  const [isFetchingSearch, setIsFetchingSearch] = useState(false);
  const loading = isFetchingSubscriptions || isFetchingSearch;
  const [isSharing, setIsSharing] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [hasMoreFirstNameResults, setHasMoreFirstNameResults] = useState(true);
  const [hasMoreLastNameResults, setHasMoreLastNameResults] = useState(true);
  const [lastFetchedItem, setLastFetchedItem] = useState<PageCursor | undefined>(undefined);
  const [lastFirstNameSearchItem, setLastFirstNameSearchItem] = useState<PageCursor | undefined>(undefined);
  const [lastLastNameSearchItem, setLastLastNameSearchItem] = useState<PageCursor | undefined>(undefined);

  const { l10n } = useLocale();
  const debouncedSearchKeyword = useDebounce(searchKeyword, COMMON_CONSTANTS.searchTextDebounce);

  // Store complete search results for client-side filtering (keyword + original results)
  // This preserves original data so backspace works correctly
  const completeSearchRef = useRef<{ keyword: string; results: ExclusiveContentSubscriptionEntity[] } | null>(null);

  // Get current user
  const currentFirebaseUser = getAuthFirebaseUserUseCase.execute();
  const currentUserId = currentFirebaseUser?.uid;
  const currentUserDisplayName = currentFirebaseUser?.displayName || '';

  // Fetch initial subscriptions on mount
  useEffect(() => {
    if (currentUserId) {
      fetchSubscriptions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUserId]);

  // Search when keyword changes
  useEffect(() => {
    // TODO: @claude add a follow up task for this: client-side search narrowing strips only outer
    // spaces (trim) — Flutter lib/presentation/bottom_sheet/search_users_bottom_sheet.dart:249-259
    // strips ALL spaces (searchValue.toLowerCase().replaceAll(" ", ""), :253 and :256), so a
    // two-word keyword like "john s" matches "johns" in Flutter and matches nothing here. The same
    // trim() also seeds completeSearchRef.current.keyword (the `keyword:` field written in
    // performSearch, useSearchUsers.ts:302) and therefore the canFilterClientSide gate
    // (:147-148 below), so a follow-up must change all three together. The server path is already
    // correct: firestoreService.ts:1341 does keyword.toLowerCase().replace(/\s/g, ''), mirroring
    // Flutter's two-step form — the lowercase at lib/data/repository/subscriptions_repository.dart:54
    // and the space strip at lib/data/data_sources/content_subscriptions_data_source.dart:155.
    const trimmedKeyword = debouncedSearchKeyword.trim().toLowerCase();

    if (trimmedKeyword) {
      // Check if we can filter client-side from a previous complete search
      const complete = completeSearchRef.current;
      const canFilterClientSide =
        complete && trimmedKeyword.startsWith(complete.keyword);

      if (canFilterClientSide) {
        console.log('Filtering search results client-side for keyword:', trimmedKeyword);
        // Filter from ORIGINAL results (not current state) so backspace works
        const filtered = complete.results.filter(
          (sub) =>
            sub.firstLastNameLower.startsWith(trimmedKeyword) ||
            sub.lastFirstNameLower.startsWith(trimmedKeyword)
        );
        setSearchResults(filtered);
        setHasMoreFirstNameResults(false);
        setHasMoreLastNameResults(false);
      } else {
        console.log('Performing new server search for keyword:', trimmedKeyword);
        // Reset and perform new server search
        setLastFirstNameSearchItem(undefined);
        setLastLastNameSearchItem(undefined);
        setHasMoreFirstNameResults(true);
        setHasMoreLastNameResults(true);
        completeSearchRef.current = null;
        performSearch(debouncedSearchKeyword, false);
      }
    } else {
      console.log('Clearing search results');
      setSearchResults([]);
      setLastFirstNameSearchItem(undefined);
      setLastLastNameSearchItem(undefined);
      setHasMoreFirstNameResults(true);
      setHasMoreLastNameResults(true);
      completeSearchRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchKeyword]);

  const fetchSubscriptions = async () => {
    // Flutter bails on `_isFetchingSubscriptions || !_hasMoreSubscriptions`
    // (search_users_bottom_sheet.dart:222). `!hasMore` is the page-exhaustion half: once the
    // subscriptions cursor is exhausted, an end-of-list trigger must issue no further query.
    if (!currentUserId || isFetchingSubscriptions || !hasMore) return;

    setIsFetchingSubscriptions(true);
    try {
      const useCase = createGetSubscriptionsUseCase();
      // The subscriptions branch deliberately pages at 9: Flutter passes
      // AppConstants.getSubscriptionsListPagination (app_constants.dart:10) here
      // (search_users_bottom_sheet.dart:231). The search branch in performSearch pages at 6 — the
      // two page sizes are intentionally different, do not unify them onto one constant.
      const result = await useCase.execute(currentUserId, lastFetchedItem, SERVER_FETCH_CONSTANTS.SEARCH_PAGINATION_LIMIT);

      // Collapse the page to one row per userId: the query carries no `meSubscriber` filter, so
      // two users subscribed to each other produce two documents with the same `userId` and a
      // different `subscriberUserId`. See `filterDuplicateSubscriptions` for the Flutter-parity
      // rationale (and for the one place web deliberately out-does Flutter here).
      const filteredSubscriptions = filterDuplicateSubscriptions(result.subscriptions);

      // From result.subscriptions filter out those already in subscriptions to avoid duplicates
      const newSubscriptions = filteredSubscriptions.filter(
        (newSub) =>
          !subscriptions.some(
            (existingSub) => existingSub.userId === newSub.userId
          )
      );

      setSubscriptions((prev) => [...prev, ...newSubscriptions]);
      setHasMore(result.hasMore);
      setLastFetchedItem(result.cursor);
    } catch (error) {
      appLogger.error('Error fetching subscriptions', 'useSearchUsers.fetchSubscriptions', { error: error as Error });
    } finally {
      setIsFetchingSubscriptions(false);
    }
  };

  const performSearch = async (keyword: string, isLoadMore: boolean) => {
    // Flutter guards the search with its own flag (search_users_bottom_sheet.dart:242).
    if (!currentUserId || isFetchingSearch) return;

    if (!hasMoreSearchResults) {
      console.log('No more search results to load');
      return;
    }

    console.log('hasMoreFirstNameResults:', hasMoreFirstNameResults);
    console.log('hasMoreLastNameResults:', hasMoreLastNameResults);

    setIsFetchingSearch(true);
    try {
      const useCase = createSearchSubscriptionsUseCase();

      // Determine which searches to execute
      const shouldSearchFirstName = !isLoadMore || hasMoreFirstNameResults;
      const shouldSearchLastName = !isLoadMore || hasMoreLastNameResults;

      // Search by firstName and lastName in parallel (only if needed)
      // The search branch deliberately pages at 6: Flutter passes
      // AppConstants.searchUsersListPagination (app_constants.dart:8) to BOTH search calls
      // (search_users_bottom_sheet.dart:272 for firstName, :285 for lastName), unlike the
      // subscriptions branch in fetchSubscriptions which pages at 9. Do not unify the two.
      const [firstNameResult, lastNameResult] = await Promise.all([
        shouldSearchFirstName
          ? useCase.execute(
            currentUserId,
            keyword,
            isLoadMore ? lastFirstNameSearchItem : undefined,
            SERVER_FETCH_CONSTANTS.SEARCH_USERS_PAGINATION_LIMIT,
            false // searchLastName = false
          )
          : null,
        shouldSearchLastName
          ? useCase.execute(
            currentUserId,
            keyword,
            isLoadMore ? lastLastNameSearchItem : undefined,
            SERVER_FETCH_CONSTANTS.SEARCH_USERS_PAGINATION_LIMIT,
            true // searchLastName = true
          )
          : null,
      ]);

      // Merge results from both searches
      const mergedResults = [
        ...(firstNameResult?.subscriptions ?? []),
        ...(lastNameResult?.subscriptions ?? []),
      ];

      // A user whose name matches on both sides comes back from BOTH queries, so the concatenation
      // above can name the same peer twice. Flutter merges the same two pages with
      // `addAllUnique` (search_users_bottom_sheet.dart:274, :288) and therefore renders one row per
      // userId; this call is that guarantee. Without it `VirtualList`'s
      // `getItemKey={(row) => row.subscription.userId}` sees a duplicate key.
      const filteredResults = filterDuplicateSubscriptions(mergedResults);

      if (isLoadMore) {
        // Filter out those already in searchResults to avoid duplicates
        const newResults = filteredResults.filter(
          (newSub) => !searchResults.some((existingSub) => existingSub.userId === newSub.userId)
        );
        setSearchResults((prev) => [...prev, ...newResults]);
      } else {
        setSearchResults(filteredResults);
      }

      const newHasMoreFirstName = firstNameResult?.hasMore ?? false;
      const newHasMoreLastName = lastNameResult?.hasMore ?? false;

      if (firstNameResult) {
        setHasMoreFirstNameResults(newHasMoreFirstName);
        setLastFirstNameSearchItem(firstNameResult.cursor);
      }
      if (lastNameResult) {
        setHasMoreLastNameResults(newHasMoreLastName);
        setLastLastNameSearchItem(lastNameResult.cursor);
      }

      // Store complete results for client-side filtering when all results fetched
      if (!isLoadMore && !newHasMoreFirstName && !newHasMoreLastName) {
        completeSearchRef.current = {
          keyword: keyword.trim().toLowerCase(),
          results: filteredResults,
        };
      }
    } catch (error) {
      appLogger.error('Error searching subscriptions', 'useSearchUsers.performSearch', { error: error as Error });
    } finally {
      setIsFetchingSearch(false);
    }
  };

  const hasMoreSearchResults = hasMoreFirstNameResults || hasMoreLastNameResults;

  const loadMoreSearchResults = () => {
    if (debouncedSearchKeyword.trim() && hasMoreSearchResults && !loading) {
      performSearch(debouncedSearchKeyword, true);
    }
  };

  /**
   * Toggles a subscription in/out of the selected-users strip.
   *
   * Flutter parity: `search_users_bottom_sheet.dart:745-749` selects with
   * `_selectedSubscriptions.insert(0, subscription)` — **prepend, not append** — and de-selects
   * with `remove(subscription)`.
   *
   * The insertion index is user-visible: the strip renders the list left-to-right
   * (`SearchUserSelectedUsersHeader.tsx:56-111`, mirroring Flutter's horizontal `ListView.builder`
   * at `:951-960`), so index 0 is the leftmost, always-visible cell. Prepending keeps the newest
   * pick in view; appending pushes it off the right edge behind the strip's horizontal scroll once
   * several users are selected. Do not "tidy" this back into `[...prev, subscription]`.
   *
   * Referentially stable on purpose. The empty dep list is correct because the body reads nothing
   * from the render scope — the updater form of `setSelectedSubscriptions` supplies `prev`, so
   * `selectedSubscriptions` is deliberately NOT a dep. This is the tail of the row-tap chain
   * `SearchUsersUsersList.renderItem` → `SearchUsersContent.handleUserClick` → here, and that chain
   * of `useCallback`s only memoizes if every link is stable; left unstable here it would rebuild
   * `renderItem` on every keystroke / `loading` flip / page append.
   */
  const toggleSubscriptionSelection = useCallback((subscription: ExclusiveContentSubscriptionEntity) => {
    setSelectedSubscriptions((prev) => {
      const isSelected = prev.some((s) => s.userId === subscription.userId);
      return isSelected
        ? prev.filter((s) => s.userId !== subscription.userId)
        : [subscription, ...prev];
    });
  }, []);

  const removeSubscription = (subscription: ExclusiveContentSubscriptionEntity) => {
    setSelectedSubscriptions((prev) =>
      prev.filter((s) => s.userId !== subscription.userId)
    );
  };

  const shareWithSelected = async (messageText: string) => {
    if (!currentUserId || selectedSubscriptions.length === 0 || !shareInfo) return;

    setIsSharing(true);
    try {
      const useCase = createShareWithFriendsUseCase();
      const success = await useCase.execute(
        shareInfo,
        selectedSubscriptions,
        messageText,
        currentUserId,
        currentUserDisplayName
      );

      if (success) {
        toast.success(l10n.share.success);
        SheetService.close();
      } else {
        toast.error(l10n.share.error);
      }
    } catch (error) {
      appLogger.error('Error sharing', 'useSearchUsers.shareWithSelected', { error: error as Error });
      toast.error(l10n.share.error);
    } finally {
      setIsSharing(false);
    }
  };

  // Search is pending when keyword differs from debounced keyword (waiting for debounce)
  const isSearchPending = searchKeyword.trim() !== debouncedSearchKeyword.trim();

  // Transform subscriptions to SearchSubscriptionEntity with isSelected flag
  const toSearchSubscriptionEntity = (sub: ExclusiveContentSubscriptionEntity): SearchSubscriptionEntity => ({
    subscription: sub,
    isSelected: selectedSubscriptions.some((s) => s.userId === sub.userId),
  });

  return {
    subscriptions: subscriptions.map(toSearchSubscriptionEntity),
    searchResults: searchResults.map(toSearchSubscriptionEntity),
    selectedSubscriptions,
    loading,
    isSearchPending,
    hasMore,
    hasMoreSearchResults,
    searchKeyword,
    isSharing,
    setSearchKeyword,
    toggleSubscriptionSelection,
    removeSubscription,
    loadMoreSubscriptions: fetchSubscriptions,
    loadMoreSearchResults,
    shareWithSelected,
  };
}
